import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import Layout from "../components/layout/Layout";
import useSeo from "../hooks/useSeo";

const STEPS = [
  {
    n: "01",
    title: "Välj lösning",
    body: "Se Vidos vanligaste alternativ och hitta den lösning som passar ditt hem.",
  },
  {
    n: "02",
    title: "Se pris",
    body: "Du ser ett tydligt paketpris direkt, så du vet nivån redan från början.",
  },
  {
    n: "03",
    title: "Skicka förfrågan",
    body: "Berätta vilken lösning du är intresserad av och var du bor.",
  },
  {
    n: "04",
    title: "Vido bekräftar",
    body: "Vido säkerställer att produkten och installationen passar fastighetens förutsättningar.",
  },
  {
    n: "05",
    title: "Installation",
    body: "Tid bokas och arbetet utförs.",
  },
];

const FAQS = [
  {
    q: "Vilka orter är ni verksamma i?",
    a: "Vi arbetar med privatpersoner i Lidköping, Götene och Skara.",
  },
  {
    q: "Vad betyder \u201Defter ROT\u201D?",
    a: "ROT-avdraget är en skattereduktion för arbetskostnad vid renovering, om- och tillbyggnad. När du har rätt till ROT drar vi av avdraget direkt på fakturan, så priset du ser är det du faktiskt betalar. Rätten till ROT är alltid din som beställare.",
  },
  {
    q: "Är priset ni visar det slutgiltiga priset?",
    a: "Priserna är exempel som utgångspunkt. Slutligt pris och exakt omfattning bekräftas alltid innan arbete påbörjas, baserat på förutsättningarna i just din fastighet.",
  },
  {
    q: "Vad ingår i installationen?",
    a: "En standardinstallation innefattar montering, inkoppling, funktionskontroll och en genomgång med dig när arbetet är klart. Vid speciella förutsättningar kan tillägg tillkomma \u2014 det bekräftas alltid innan arbetet påbörjas.",
  },
  {
    q: "Hur snabbt kan ni installera?",
    a: "Efter att förutsättningarna är bekräftade bokar vi in en tid som passar dig. Ledtiden varierar beroende på säsong och tillgänglighet.",
  },
  {
    q: "Hur kontaktar jag Vido?",
    a: "Fyll i förfrågningsformuläret här på sajten, ring 079-322 81 87 eller mejla mikael@vido.nu.",
  },
];

export default function SaFungerarDet() {
  useSeo({
    title: "Så fungerar det | Vido Klimatteknik",
    description:
      "Från val till installation \u2014 fem tydliga steg med Vido Klimatteknik.",
    path: "/sa-fungerar-det",
  });

  return (
    <Layout>
      {/* Hero */}
      <section
        data-testid="sfd-hero"
        className="pt-12 md:pt-16 pb-8 md:pb-10"
      >
        <div className="container-vido max-w-3xl fade-in">
          <span className="eyebrow">Så fungerar det</span>
          <h1 className="display-h1 mt-5">
            Från val till installation —{" "}
            <span className="italic text-[color:hsl(var(--accent-terracotta))]">
              fem tydliga steg
            </span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[color:hsl(var(--muted-ink))] max-w-[58ch] leading-relaxed">
            Ett rakt flöde utan öppna kostnader och utan att du behöver jaga
            svar. Så här ser vägen ut.
          </p>
        </div>
      </section>

      {/* 4 steps — vertical timeline */}
      <section
        data-testid="sfd-steps"
        className="py-8 md:py-12"
      >
        <div className="container-vido">
          <ol className="relative max-w-4xl mx-auto">
            <span
              aria-hidden
              className="hidden md:block absolute left-[112px] top-6 bottom-6 w-px bg-[color:hsl(var(--line))]"
            />
            {STEPS.map((s, i) => (
              <li
                key={s.n}
                data-testid={`sfd-step-${i + 1}`}
                className="relative grid md:grid-cols-[112px_1fr] gap-6 md:gap-8 items-start py-6 md:py-8 fade-in"
              >
                <div className="md:pr-6 md:text-right">
                  <span className="font-serif text-5xl md:text-6xl font-medium text-[color:hsl(var(--accent-terracotta))] leading-none">
                    {s.n}
                  </span>
                </div>
                <div className="md:pl-8 md:border-l md:border-[color:hsl(var(--line))] md:relative">
                  <span
                    aria-hidden
                    className="hidden md:block absolute -left-[7px] top-3 w-3.5 h-3.5 rounded-full bg-[color:hsl(var(--accent-terracotta))] ring-4 ring-[hsl(var(--surface))]"
                  />
                  <h3 className="font-serif text-2xl md:text-3xl font-medium leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-base text-[color:hsl(var(--muted-ink))] leading-relaxed max-w-[52ch]">
                    {s.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="sfd-faq"
        className="section-y bg-white border-y border-[color:hsl(var(--line))]"
      >
        <div className="container-vido max-w-3xl">
          <div className="fade-in">
            <span className="eyebrow">FAQ</span>
            <h2 className="display-h2 mt-4">Vanliga frågor</h2>
          </div>
          <Accordion
            type="single"
            collapsible
            className="mt-8 fade-in"
            data-testid="faq-accordion"
          >
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-[color:hsl(var(--line))] last:border-b-0"
              >
                <AccordionTrigger className="py-4 md:py-5 text-left text-base md:text-lg font-medium text-[color:hsl(var(--ink))] hover:no-underline [&>svg]:text-[color:hsl(var(--accent-terracotta))] [&>svg]:h-5 [&>svg]:w-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pt-1 text-sm md:text-base text-[color:hsl(var(--muted-ink))] leading-relaxed max-w-[62ch]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-14" data-testid="sfd-cta">
        <div className="container-vido">
          <div className="text-center max-w-2xl mx-auto fade-in">
            <span className="eyebrow">Nästa steg</span>
            <h2 className="display-h2 mt-4">Redo att välja lösning?</h2>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to="/produkter"
                data-testid="sfd-cta-primary"
                className="btn-primary"
              >
                Se våra lösningar
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/kontakt"
                data-testid="sfd-cta-secondary"
                className="btn-secondary"
              >
                Kontakta Vido
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
